import React, { PureComponent, Fragment } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { CheckRounded, CloseRounded, CreateRounded } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import withTheme from "@mui/styles/withTheme";

import { Input } from "./styled/Input";
import { ModalConfirm } from "./ModalConfirm";
import { Td } from "./styled/Table/Td";

export class EditableTdComponent extends PureComponent {
    constructor(props) {
        super(props);

        this.inputContainer = React.createRef();
        this.inputField = React.createRef();
    }

    state = {
        confirmModalIsOpen: false,
        editMode: false,
        originalValue: this.props.children.toString(),
        value: this.props.children.toString()
    };

    componentDidUpdate(prevProps) {
        if (prevProps.children.toString() !== this.props.children.toString()) {
            this.setState({ value: this.props.children.toString() });
        }

        if (
            this.state.value !== this.state.originalValue &&
            this.props.requestStatus !== prevProps.requestStatus
        ) {
            if (this.props.requestStatus === "DONE") {
                this.setState({ originalValue: this.state.value });
            }

            if (this.props.requestStatus === "FAILED") {
                this.setState({ value: this.state.originalValue });
            }
        }
    }

    componentWillUnmount() {
        document.removeEventListener("mousedown", this.handleClickOutside);
    }

    handleClickOutside = evt => {
        if (
            !this.state.confirmModalIsOpen &&
            this.inputContainer &&
            !this.inputContainer.current.contains(evt.target)
        ) {
            if (this.props.saveOnBlur && this.state.value !== this.state.originalValue) {
                this.handleConfirm();
            }
            this.handleCancel();
        }
    };

    handleSubmit = evt => {
        if (evt !== undefined && evt.preventDefault) {
            evt.preventDefault();
        }

        if (this.state.value !== this.state.originalValue) {
            return this.props.confirmAction
                ? this.setState({ confirmModalIsOpen: true })
                : this.handleConfirm();
        }

        this.handleCancel();
    };

    handleConfirm = () => {
        this.setState({ confirmModalIsOpen: false, editMode: false });
        document.removeEventListener("mousedown", this.handleClickOutside);
        this.props.onConfirm(this.state.value, this.props);
    };

    handleCancel = () => {
        this.setState({
            confirmModalIsOpen: false,
            editMode: false,
            value: this.state.originalValue
        });
        document.removeEventListener("mousedown", this.handleClickOutside);
    };

    handleEdit = () => {
        this.setState({ editMode: true });
        document.addEventListener("mousedown", this.handleClickOutside);
    };

    updateValue = event => {
        const { value } = event.target;
        this.setState({ value });
    };

    render() {
        if (this.state.editMode) {
            const { palette = { controls: {} } } = this.props.theme;
            const { controls = {} } = palette;
            const { cancel, confirm } = controls;

            return (
                <Fragment>
                    <ModalConfirm
                        isOpen={this.state.confirmModalIsOpen}
                        onRequestClose={this.handleCancel}
                        onConfirm={this.handleConfirm}
                    />
                    <td ref={this.inputContainer} data-testid="editable-td">
                        <Form>
                            <EditInput
                                ref={this.inputField}
                                type="text"
                                value={this.state.value}
                                onChange={this.updateValue}
                            />
                            <IconButton
                                type="submit"
                                onClick={this.handleSubmit}
                                aria-label="Submit change"
                                size="small"
                            >
                                <CheckRounded
                                    fontSize="large"
                                    htmlColor={confirm}
                                    aria-hidden="true"
                                />
                            </IconButton>
                            <IconButton
                                onClick={this.handleCancel}
                                aria-label="Cancel change"
                                size="small"
                            >
                                <CloseRounded
                                    fontSize="large"
                                    aria-hidden="true"
                                    htmlColor={cancel}
                                />
                            </IconButton>
                        </Form>
                    </td>
                </Fragment>
            );
        }

        return (
            <EditTd onClick={this.handleEdit} data-testid="editable-td">
                {this.state.value}
                <CreateRounded id="edit-icon" aria-hidden="true" />
            </EditTd>
        );
    }
}

export const Form = styled.span`
    align-items: center;
    display: flex;
    flex-flow: row nowrap;
`;

export const ButtonIcon = styled.button`
    background: transparent;
    border: 0;
    cursor: pointer;
    margin: 0;
    outline: 0;
    padding: 0;
`;

export const EditInput = styled(Input)`
    min-width: 0;
    border-radius: 0.3rem;
    border: 0;
    box-shadow: inset 0 0 0 1px ${props => props.theme.primary};
    color: #000;
    font-size: 1.2rem;
    margin: 0;
`;

const EditTd = styled(Td)`
    position: relative;

    #edit-icon {
        position: absolute;
        right: 5px;
        top: 10px;
        opacity: 0;
    }

    &:hover {
        #edit-icon {
            opacity: 1;
        }
    }

    @media print {
        i,
        svg {
            display: none !important;
        }
    }
`;

EditableTdComponent.propTypes = {
    saveOnBlur: PropTypes.bool,
    children: PropTypes.node.isRequired,
    confirmAction: PropTypes.bool,
    onConfirm: PropTypes.func.isRequired,
    requestStatus: PropTypes.string,
    theme: PropTypes.object
};

export const EditableTd = withTheme(EditableTdComponent);
